import { StepEditorProps } from "../useStepEditor";
import { Block, TextInput } from "../../../../components/atoms";
import { ErrorMessage, Field, InputHandle, Stack } from "@introist/react-foundation/v2";
import { prefixForm } from "../../../../utils/FormUtils";
import { StepEditorAccordion } from "../../../workflows/routes/WorkflowEditor/StepEditorAccordion";
import { MeetingParticipantsEditor } from "./MeetingParticipants";
import { MessageEditor } from "../../../../components/editors/MessageEditor";
import { ContentType } from "../../../AIAssistant/schema";
import { MeetingSchedule } from "./MeetingSchedule";
import { MeetingSettings } from "./MeetingSettings";
import { MeetingLocationEditor } from "./LocationEditor";
import { TagSelector } from "../../../system/tags/TagSelector";
import { Tag } from "../../../system/tags/useTags";
import { useCallback, useRef } from "react";

export const MeetingStepEditor = ({
  form,
  stepDataPath,
  targetDateTerm,
  workflowId
}: StepEditorProps) => {
  const prefixedForm = prefixForm(form, stepDataPath);

  const titleRef = useRef<InputHandle>(null);

  const addTitleTag = useCallback(
    (tag: Tag) => {
      if (!titleRef.current) return;
      titleRef.current.insertTextToCursorPosition(`{{${tag.tag}}}`);
    },
    [titleRef]
  );

  const tagContext = { workflowId, expandRoots: ["datasets"] };

  return (
    <>
      <Block blended compact>
        <StepEditorAccordion title="Meeting time">
          <MeetingSchedule
            form={form}
            stepDataPath={stepDataPath}
            targetDateTerm={targetDateTerm}
            workflowId={workflowId}
          />
        </StepEditorAccordion>
      </Block>

      <Block blended compact>
        <MeetingParticipantsEditor form={form} formPrefix={stepDataPath} workflowId={workflowId} />
      </Block>
      <Block blended compact>
        <MeetingLocationEditor form={form} formPrefix={stepDataPath} />
      </Block>
      <Block compact blended>
        <StepEditorAccordion title="Meeting details">
          <Stack vertical gap="xLarge">
            <Field title="Title">
              <TextInput
                inputHandleRef={titleRef}
                size="small"
                placeholder="Title"
                value={prefixedForm.get("title")}
                endAdornment={
                  <TagSelector title="" size="small" ctx={tagContext} onSelect={addTitleTag} />
                }
                onChange={prefixedForm.set("title")}
                error={prefixedForm.isError("title")}
              />
            </Field>
            <Field title="Description">
              <MessageEditor
                message={prefixedForm.get("description")}
                onChange={prefixedForm.set("description")}
                contentType={ContentType.MeetingDescription}
                tags={tagContext}
              />
              {prefixedForm.error("description") && (
                <ErrorMessage>{prefixedForm.error("description")}</ErrorMessage>
              )}
            </Field>
          </Stack>
        </StepEditorAccordion>
      </Block>
      <MeetingSettings form={form} stepDataPath={stepDataPath} workflowId={workflowId} />
    </>
  );
};
